import { useMemo, useState } from 'react';
import type { BrandStatus } from '../../types';

export interface HeritageOption {
  id: string;
  /** Display label (displayName || name). */
  name: string;
  status?: BrandStatus;
  /** Secondary line shown muted to the right (e.g. the doc id / slug). */
  subtitle?: string;
}

interface HeritagePickerProps {
  label: string;
  helpText: string;
  /** Shown when nothing is selected, e.g. "No heritage brands". */
  emptyLabel: string;
  values: string[];
  onChange: (next: string[]) => void;
  /**
   * All candidate entities. The selectable typeahead is restricted to those with
   * `status: 'defunct'` or `status: 'absorbed'`; already-selected ids are always
   * shown as chips (even if their status later changed) so they stay removable.
   */
  options: HeritageOption[];
  loading?: boolean;
  /** Word used in the search placeholder / list copy, e.g. "brands". */
  noun?: string;
}

/**
 * Shared multi-select for heritage / lineage links. A modern (active) entity
 * points at the defunct entities whose line it carries on (e.g. Sherpa Watches →
 * Enicar). The constraint that only `status: 'defunct'` or `status: 'absorbed'`
 * entities can be linked is enforced here in the UI only — the data layer is
 * untouched.
 */
export function HeritagePicker({
  label,
  helpText,
  emptyLabel,
  values,
  onChange,
  options,
  loading = false,
  noun = 'brands',
}: HeritagePickerProps) {
  const [search, setSearch] = useState('');

  const optionById = useMemo(() => {
    const m = new Map<string, HeritageOption>();
    for (const o of options) m.set(o.id, o);
    return m;
  }, [options]);

  const selectedSet = useMemo(() => new Set(values), [values]);

  // Only defunct or absorbed entities are selectable in the typeahead.
  const selectableOptions = useMemo(
    () =>
      options
        .filter((o) => o.status === 'defunct' || o.status === 'absorbed')
        .sort((a, b) => a.name.localeCompare(b.name)),
    [options],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return selectableOptions;
    return selectableOptions.filter((o) =>
      [o.name, o.id, o.subtitle ?? ''].join(' ').toLowerCase().includes(q),
    );
  }, [selectableOptions, search]);

  const toggle = (id: string) => {
    const next = new Set(selectedSet);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange(Array.from(next).sort());
  };

  const removeChip = (id: string) => {
    onChange(values.filter((v) => v !== id));
  };

  // Selected ids whose entity is no longer defunct (or is unknown) — flagged but
  // still removable. This is a data-integrity question for later, not a hard block.
  const nonDefunctSelected = values.filter(
    (id) => optionById.get(id)?.status !== 'defunct',
  );

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label}
      </label>
      <p className="text-xs text-gray-500 mb-2">{helpText}</p>

      {/* Selected chips / empty state */}
      {values.length > 0 ? (
        <div className="flex flex-wrap gap-1.5 mb-2">
          {values.map((id) => {
            const o = optionById.get(id);
            const chipLabel = o ? o.name : id;
            const isDefunct = o?.status === 'defunct';
            return (
              <span
                key={id}
                className={`inline-flex items-center gap-1 text-xs rounded-full pl-2 pr-1 py-0.5 border ${
                  isDefunct
                    ? 'bg-amber-50 border-amber-200 text-amber-800'
                    : 'bg-red-50 border-red-200 text-red-800'
                }`}
                title={isDefunct ? id : `${id} — no longer marked defunct`}
              >
                <span>{chipLabel}</span>
                <button
                  type="button"
                  onClick={() => removeChip(id)}
                  aria-label={`Remove ${chipLabel}`}
                  className="rounded-full hover:bg-black/5 w-4 h-4 inline-flex items-center justify-center"
                >
                  ×
                </button>
              </span>
            );
          })}
        </div>
      ) : (
        <p className="text-xs text-gray-400 italic mb-2">{emptyLabel}</p>
      )}

      <input
        type="text"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder={loading ? `Loading ${noun}…` : `Search defunct ${noun}…`}
        disabled={loading}
        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-500 focus:border-brand-500 mb-1"
      />

      <div className="border border-gray-200 rounded-lg max-h-48 overflow-y-auto">
        {loading ? (
          <div className="p-3 text-sm text-gray-500">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="p-3 text-sm text-gray-500">
            {selectableOptions.length === 0
              ? `No defunct ${noun} to link.`
              : `No defunct ${noun} match.`}
          </div>
        ) : (
          <ul className="divide-y divide-gray-100">
            {filtered.map((o) => {
              const checked = selectedSet.has(o.id);
              return (
                <li key={o.id}>
                  <label className="flex items-center gap-2 px-3 py-1.5 cursor-pointer hover:bg-gray-50">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggle(o.id)}
                      className="rounded text-brand-600 focus:ring-brand-500"
                    />
                    <span className="text-sm text-gray-800 flex-1">{o.name}</span>
                    {o.subtitle && (
                      <code className="text-xs text-gray-400">{o.subtitle}</code>
                    )}
                  </label>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {nonDefunctSelected.length > 0 && (
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mt-1">
          Linked but no longer marked defunct: {nonDefunctSelected.join(', ')}.
          Heritage links should point at defunct entities — review when convenient.
        </p>
      )}
    </div>
  );
}
